import { type PageBase, getCurrentPage } from '@avensia/excite';
import { pageToDataLayerInfo, getRetailMediaSessionId, getTrackingTimestamp } from '../utils';
import { addToDataLayer, type DataLayerEvent } from '..';
import * as React from 'react';
import { type RefObject, useEffect } from 'react';

export type RetailMediaProductEventType = {
  adId?: string;
  isSponsoredProduct?: boolean;
};

export function addSelectRetailMediaBannerToDataLayer(adId: string | undefined) {
  const page = getCurrentPage() as PageBase;
  const ga4Event: DataLayerEvent = {
    event: 'retail_media_ad_select',
    ecommerce: {
      ...pageToDataLayerInfo(page),
      retail_media_ad_id: adId,
      retail_media_session_id: getRetailMediaSessionId(),
      timestamp: getTrackingTimestamp(),
    },
  };

  addToDataLayer(ga4Event);
}

export function addViewRetailMediaBannerToDataLayer(data: RetailMediaProductEventType) {
  const page = getCurrentPage() as PageBase;
  const ga4Event: DataLayerEvent = {
    event: 'retail_media_ad_view',
    ecommerce: {
      ...pageToDataLayerInfo(page),
      retail_media_ad_id: data?.adId,
      retail_media_session_id: getRetailMediaSessionId(),
      timestamp: getTrackingTimestamp(),
    },
  };

  addToDataLayer(ga4Event);
}

export function addSelectRetailMediaSponsoredProductToDataLayer(adId: string | undefined) {
  const page = getCurrentPage() as PageBase;
  const ga4Event: DataLayerEvent = {
    event: 'retail_media_ad_select',
    ecommerce: {
      ...pageToDataLayerInfo(page),
      retail_media_ad_id: adId,
      retail_media_session_id: getRetailMediaSessionId(),
      timestamp: getTrackingTimestamp(),
    },
  };

  addToDataLayer(ga4Event);
}

export function addViewRetailMediaSponsoredProductToDataLayer(data: RetailMediaProductEventType) {
  if (data.isSponsoredProduct) {
    const page = getCurrentPage() as PageBase;
    const ga4Event: DataLayerEvent = {
      event: 'retail_media_ad_view',
      ecommerce: {
        ...pageToDataLayerInfo(page),
        retail_media_ad_id: data?.adId,
        retail_media_session_id: getRetailMediaSessionId(),
        timestamp: getTrackingTimestamp(),
      },
    };

    addToDataLayer(ga4Event);
  }
}

export function addRetailMediaSponsoredProductAddToCartToDataLayer(adId: string | undefined) {
  const page = getCurrentPage() as PageBase;
  const ga4Event: DataLayerEvent = {
    event: 'retail_media_add_to_cart',
    ecommerce: {
      ...pageToDataLayerInfo(page),
      retail_media_ad_id: adId,
      retail_media_session_id: getRetailMediaSessionId(),
      timestamp: getTrackingTimestamp(),
    },
  };

  addToDataLayer(ga4Event);
}

export function useRetailMediaViewEventTrigger<T>(
  ref: React.RefObject<HTMLElement | null>,
  addToDataLayer: (payload: T) => void,
  payload: T,
  resetTriggerOn: any = null,
  shouldTrack: boolean = true,
) {
  const hasTriggeredRef = React.useRef(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | undefined>(undefined);

  React.useEffect(() => {
    if (ref?.current && shouldTrack) {
      const observer = new IntersectionObserver(
        async ([entry], obs) => {
          if (entry.intersectionRatio >= 0.5) {
            if (!hasTriggeredRef.current && !timeoutRef.current) {
              timeoutRef.current = setTimeout(() => {
                if (!hasTriggeredRef.current) {
                  addToDataLayer(payload);
                  hasTriggeredRef.current = true;
                }
                obs.unobserve(entry.target);
              }, 1000);
            }
          } else {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = undefined;
            }
          }
        },
        { threshold: 0.5 },
      );

      observer.observe(ref.current);
      return () => {
        observer.disconnect();
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }
  }, [ref, addToDataLayer, payload, shouldTrack]);

  useEffect(() => {
    hasTriggeredRef.current = false;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, [resetTriggerOn]);

  const resetTrigger = React.useCallback(() => {
    hasTriggeredRef.current = false;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  return [resetTrigger];
}
